/**
 * 路由可见性（体验层）：菜单与首页快捷入口共用。真正拦截在后端 requirePermission。
 */
import { PERMISSIONS as P } from '../contracts/permissions';
import type { Permission } from '../contracts/permissions';
import { ROLE_PERMISSIONS } from '../contracts/roles';
import type { AdminRole } from '../contracts/roles';

// 仅超级管理员可见的叶子路由 key（非超管时从菜单中过滤掉）。
// 后端对应端点同样二次校验 super_admin —— 菜单隐藏只是体验层。
const SUPER_ADMIN_ONLY_KEYS = new Set<string>(['/users/list-all']);

// 叶子路由 → 进页面加载数据所需的读权限（持有任一即显示）。取自各页列表接口的
// requirePermission：没有它点进去只会是 403 空页，不如不显示（observer 尤甚）。
// 未列出的 key 不过滤。同样只是体验层，真正拦截在后端。
const MENU_READ_PERMISSIONS: Record<string, Permission[]> = {
  '/dashboard/north-star': [P.DASHBOARD_READ],
  '/dashboard/print': [P.DASHBOARD_READ],
  '/dashboard/content': [P.DASHBOARD_READ],
  '/dashboard/creation-funnel': [P.DASHBOARD_READ],
  '/orders': [P.ORDER_READ],
  '/refunds': [P.REFUND_INITIATE, P.REFUND_APPROVE, P.REFUND_EXECUTE],
  '/packages': [P.PACKAGE_READ],
  '/reconciliation': [P.RECONCILIATION_READ],
  '/users': [P.USER_READ],
  '/lifecycle': [P.USER_READ, P.USER_RESTORE],
  '/boost': [P.BOOST_READ, P.BOOST_READ_RELATIONSHIPS],
  '/vouchers': [P.VOUCHER_READ],
  // 发券申请（运营主管提交 / 超管审批）：持任一点位即显示；真正拦截在后端
  '/vouchers/requests': [P.VOUCHER_REQUEST, P.VOUCHER_APPROVE],
  // 兑换码页返回所有批次的明文未用码：单独的 redeem_code:read，不跟 voucher:read 走（/vouchers 仍是 voucher:read）
  '/redeem-codes': [P.REDEEM_CODE_READ],
  '/partners': [P.PARTNER_READ],
  '/settlements': [P.SETTLEMENT_READ],
  '/shares': [P.SHARE_READ],
  '/ai-tasks': [P.AI_TASK_READ],
  '/prompts': [P.PROMPT_READ],
  '/chapter-rollback': [P.CONTENT_READ],
  '/tickets': [P.TICKET_READ],
  '/admins': [P.ADMIN_READ],
  '/feature-flags': [P.CONFIG_READ],
  '/audit-logs': [P.AUDIT_LOG_READ],
  '/advisor/customers': [P.ADVISOR_CUSTOMER_READ],
  // A-5 交叉用户：只有超管（advisor:manage）；销售看不到合伙人信息
  '/advisor/overlaps': [P.ADVISOR_MANAGE],
  // A-4 销售管理：超管（advisor:manage）与运营主管（advisor:team_view，团队概览 + 受约束转交）；销售只看自己名下，看不到别的销售
  '/advisor/sales': [P.ADVISOR_MANAGE, P.ADVISOR_TEAM_VIEW],
  // 销售账号（招销售：建号 / 绑企微 / 停用启用）：运营主管与超管；只能碰 sales 账号
  '/advisor/sales-accounts': [P.SALES_ACCOUNT_MANAGE],
  // 顾问设置（开单金额区间）：只有超管能调
  '/advisor/settings': [P.ADVISOR_MANAGE],
};

export function canSeeRoute(role: string | null, key: string): boolean {
  if (role === 'super_admin') return true;
  if (SUPER_ADMIN_ONLY_KEYS.has(key)) return false;
  const required = MENU_READ_PERMISSIONS[key];
  if (!required) return true;
  const perms: Permission[] =
    role && Object.prototype.hasOwnProperty.call(ROLE_PERMISSIONS, role)
      ? ROLE_PERMISSIONS[role as AdminRole]
      : [];
  return required.some((p) => perms.includes(p));
}
